export const WORKING_FOLDER_MAX_FILES = 8;
export const WORKING_FOLDER_MAX_FILE_BYTES = 128 * 1024;
export const WORKING_FOLDER_MAX_CONTEXT = 3000;
export const WORKING_FOLDER_MAX_CANDIDATES = 200;
const TEXT_EXTENSIONS = new Set([
    'txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'yaml', 'yml', 'xml', 'html', 'htm',
    'css', 'scss', 'less', 'js', 'jsx', 'mjs', 'cjs', 'ts', 'tsx', 'py', 'cs', 'java',
    'c', 'cpp', 'h', 'hpp', 'rs', 'go', 'rb', 'php', 'ps1', 'sh', 'sql', 'toml', 'rst',
]);
const EXCLUDED_SEGMENTS = new Set(['.git', '.hg', '.svn', 'node_modules', '.ssh', '.aws']);
/** Accept only short relative browser paths to supported text files. */
export function workingFolderPath(file) {
    const path = (file.webkitRelativePath || file.name).replace(/\\/g, '/');
    if (!path || path.length > 360 || path.startsWith('/') || /^[a-z]:/i.test(path) || /[\u0000-\u001f\u007f]/.test(path))
        return null;
    if (!Number.isFinite(file.size) || file.size < 0 || file.size > WORKING_FOLDER_MAX_FILE_BYTES)
        return null;
    const segments = path.split('/');
    if (segments.some((segment) => !segment || segment === '.' || segment === '..' || segment.includes(':')))
        return null;
    if (segments.some((segment) => EXCLUDED_SEGMENTS.has(segment.toLowerCase()) || /^\.env(?:\.|$)/i.test(segment)))
        return null;
    const filename = segments[segments.length - 1];
    const extension = filename.includes('.') ? filename.slice(filename.lastIndexOf('.') + 1).toLowerCase() : '';
    if (!TEXT_EXTENSIONS.has(extension))
        return null;
    return path;
}
/** Inspect file metadata only; listing a folder never reads or sends file contents. */
export function listWorkingFolderCandidates(files) {
    const candidates = [];
    const seen = new Set();
    let skipped = 0;
    for (const file of files) {
        const path = workingFolderPath(file);
        if (!path || seen.has(path) || candidates.length >= WORKING_FOLDER_MAX_CANDIDATES) {
            skipped += 1;
            continue;
        }
        seen.add(path);
        candidates.push({ file, path });
    }
    return { candidates, skipped };
}
/** Read only explicitly selected files and construct a bounded, all-or-nothing context update. */
export async function buildWorkingFolderContext(selected) {
    if (selected.length === 0)
        throw new Error('Select at least one text file.');
    if (selected.length > WORKING_FOLDER_MAX_FILES)
        throw new Error(`Choose up to ${WORKING_FOLDER_MAX_FILES} files.`);
    const seen = new Set();
    for (const entry of selected) {
        if (workingFolderPath(entry.file) !== entry.path || seen.has(entry.path))
            throw new Error('The selected files include an unsupported or invalid path. Choose the folder again.');
        seen.add(entry.path);
    }
    const roots = selected.map((entry) => entry.path.includes('/') ? entry.path.split('/')[0] : 'Selected files');
    if (!roots.every((root) => root === roots[0]))
        throw new Error('Choose files from one working folder.');
    const name = roots[0].slice(0, 120);
    const contents = await Promise.all(selected.map(async (entry) => {
        let text;
        try {
            text = await entry.file.text();
        }
        catch {
            throw new Error(`Could not read ${entry.path}. Your previous working folder has been kept.`);
        }
        if (new TextEncoder().encode(text).byteLength > WORKING_FOLDER_MAX_FILE_BYTES)
            throw new Error(`${entry.path} exceeds the 128 KB text limit.`);
        if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(text))
            throw new Error(`${entry.path} does not appear to be a plain text file.`);
        return text.replace(/^\uFEFF/, '');
    }));
    const paths = selected.map((entry) => entry.path.includes('/') ? entry.path.slice(entry.path.indexOf('/') + 1) : entry.path);
    const prefix = `Working folder: ${name}\nSelected read-only text excerpts:\n`;
    const labels = paths.map((path) => `\n--- ${path} ---\n`);
    const truncationMarker = '\n[Excerpt truncated]';
    let remaining = WORKING_FOLDER_MAX_CONTEXT - prefix.length - labels.reduce((sum, label) => sum + label.length, 0) - selected.length * truncationMarker.length;
    if (remaining < selected.length)
        throw new Error('The selected file names leave no room for text. Choose fewer files.');
    const files = contents.map((content, index) => {
        const allowance = Math.floor(remaining / (contents.length - index));
        const text = content.slice(0, allowance);
        remaining -= text.length;
        return { path: paths[index], text, truncated: text.length < content.length };
    });
    const context = prefix + files.map((file, index) => labels[index] + file.text + (file.truncated ? truncationMarker : '')).join('');
    return { name, files, context };
}
//# sourceMappingURL=workingFolder.js.map